import { Button, Form, Input, message, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import {
    IAllEmplyoee,
    IEntitlementState,
    ILeavePeriod,
    ILeaveType,
    IReviewEntitlementPayload
} from "../leave-entitlement.types";
import server from "../../../../../server";
import { formatEnum } from "../../../../../utility/utility";
import Grid from "../../../../../common/table";
import { Eye, Info } from "lucide-react";

interface BulkLeaveEntitlementProps {
    entitlementState: IEntitlementState;
    setEntitlementState: React.Dispatch<React.SetStateAction<IEntitlementState>>;
    leavePeriod: ILeavePeriod[];
    refreshEntitlements: () => void;
}

const AddBulkLeaveEntitlement = ({entitlementState, setEntitlementState, leavePeriod, refreshEntitlements}: BulkLeaveEntitlementProps) => {
    const [employees, setEmployees] = useState<IAllEmplyoee[]>([])
    const [bulkEntilementForm] = Form.useForm();
    const [data, setData] = useState([]);

    const Columns = [
        {
            title: "Employee Name",
            dataIndex: "employeeName",
            key: "employeeName",
        },
        {
            title: "Old Entitlements",
            dataIndex: "oldEntitlementUnits",
            key: "oldEntitlementUnits",
        },
        {
            title: "New Entitlements",
            dataIndex: "newAllocatedUnits",
            key: "newAllocatedUnits",
        },
        {
            title: "Total Units",
            dataIndex: "totalUnits",
            key: "totalUnits",
        },
    ]

    const handleCancel = () => {
        setEntitlementState((prev: any) => (
            {
                ...prev,
                addBulkEntitlementModal: {
                    ...prev.addBulkEntitlementModal,
                    open: false
                }
            }
        ))
        bulkEntilementForm.resetFields();
        setData([]);
    }

    async function getAllEmployee() {
        const allEmployees = await server.employee.getAllEmployee();
        setEmployees(allEmployees)
    }

    useEffect(() => {
        getAllEmployee()
    }, [])

    async function reviewEntitlements() {
        try {
            const values = await bulkEntilementForm.validateFields();

            const payload: IReviewEntitlementPayload = {
                leaveType: values.leaveType,
                unit: Number(values.unit),
                leavePeriodId: values.leavePeriodId,
                employeeIds: values.employeeIds,
            };

            const response = await server.leave.entitlement.bulkAssignReview(payload);
            setData(response);

        } catch (error) {
            message.error("Please fill all the required fields!");
        }
    }

    async function bulkEntitlementAssign() {
        try {
            const values = bulkEntilementForm.getFieldsValue();
            const payload: IReviewEntitlementPayload = {
                leaveType: values.leaveType,
                unit: Number(values.unit),
                leavePeriodId: values.leavePeriodId,
                employeeIds: values.employeeIds,
            }
            await server.leave.entitlement.bulkEntitlementAssign(payload)
            message.success("Entitlements assigned successfully!")
            // update table imediately after bulk leave entitlememt assignment
            refreshEntitlements();
            handleCancel();
        } catch (error) {
            console.log("error")
        }
    }

    return (
        <>
            <Modal
                title="Add Bulk Leave Entitlement"
                open={entitlementState.addBulkEntitlementModal.open}
                onCancel={() => handleCancel()}
                className={"hrms-app bulk-entitlement-modal"}
                onOk={bulkEntitlementAssign}
            >
                <div className={'bulk-entitlement-modal__description'}>
                    <Info className={'info-icon'}/>
                    <p className={'info-content'}>Leave entitlements set here will override existing balances for the
                        selected period. Review carefully before confirming.
                    </p>
                </div>

                <Form form={bulkEntilementForm} layout={"vertical"}>
                    <Form.Item
                        label={"Employee"}
                        name={'employeeIds'}
                        rules={[{required: true, message: 'Please select Employees'}]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select employees"
                            maxTagCount={3} // show only 3 names in box instead of showing all employee names
                            maxTagPlaceholder={(selectedValues) => `+${selectedValues.length} more`}
                            onChange={(selectedValues) => {
                                if (selectedValues.includes("ALL")) {
                                    const allIds = employees.map(emp => emp.id);
                                    bulkEntilementForm.setFieldsValue({
                                        employeeIds: allIds
                                    });
                                } else {
                                    bulkEntilementForm.setFieldsValue({
                                        employeeIds: selectedValues
                                    });
                                }
                            }}
                        >
                            <Select.Option key="ALL" value="ALL">
                                Select All
                            </Select.Option>
                            {employees.map((emp: IAllEmplyoee) => (
                                <Select.Option key={emp.id} value={emp.id}>
                                    {emp.name.firstName} {emp.name.middleName} {emp.name.lastName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <div className={'leave-detail-wrapper'}>
                        <Form.Item
                            label="Leave Type"
                            name="leaveType"
                            rules={[{required: true, message: 'Please input Leave Type!'}]}>
                            <Select
                                placeholder={"Select leave type"}
                                disabled={entitlementState.modalConfig.fieldDisabled}
                            >
                                {entitlementState.activeLeaves?.map((leaveType: ILeaveType, index: number) => (
                                    <Select.Option value={leaveType.type} key={leaveType.id}>
                                        {formatEnum(leaveType.type)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label={"Select Leave Period"}
                            name={"leavePeriodId"}
                            required={true}
                            rules={[
                                {
                                    required: true,
                                    message: "Please select Leave Period",
                                },
                            ]}
                        >
                            <Select
                                placeholder="Select leave period"
                            >
                                {leavePeriod.map((leavePeriod: ILeavePeriod) => (
                                    <Select.Option key={leavePeriod.id}
                                                   value={leavePeriod.id}>
                                        {leavePeriod.displayName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item
                        className={'unit-input'}
                        label="Unit"
                        name={"unit"}
                        rules={[{required: true, message: 'Please input Unit!'}]}>
                        <Input
                            placeholder={"Enter units"}
                            name={"unit"}
                        />
                    </Form.Item>
                </Form>

                <Button
                    className={'review-entitlement-btn'}
                    type="primary"
                    onClick={() => reviewEntitlements()}
                >
                    <Eye/>
                    Review Entitlements
                </Button>
                {data.length > 0 && (
                    <div className="review-entitlement-table">
                        <Grid
                            rowKey="id"
                            bordered={true}
                            dataSource={data}
                            columns={Columns}
                        />
                    </div>
                )}
            </Modal>
        </>
    );
};

export default AddBulkLeaveEntitlement;
